import * as Effects from 'redux-saga/effects'
import { select } from 'redux-saga/effects'
import { OverviewActionNames } from '../../reducers/overview/actionNames'
import {
  getOverviewDifferencePeriod,
  getOverviewSelectedDates,
  getOverviewSelectedIndex,
  getOverviewSelectedRooms,
} from '../../reducers/overview/selectors'
import { getSortedRoomsByPosition } from '../../reducers/bookingCalendar/selectors/rooms'
import overviewPeriod from '../../utils/Date/overviewPeriod'
import { inDateRange } from '../../utils/GridCreator/makeGrid'
import {
  addDays,
  addSeconds,
  differenceInCalendarDays,
  max,
  min,
} from 'date-fns'
import { EventStatus, IEvent } from '../../models/Event'
import { groupBy } from 'lodash'
import { UserAccountActions } from '../../reducers/userAccount/actionNames'
import { notRemovedAndUnavailableEvents } from '../../reducers/bookingCalendar/selectors/events'

export default function*() {
  yield Effects.debounce(
    200,
    [
      OverviewActionNames.SetSelectedIndex,
      OverviewActionNames.ToggleSelectedRoom,
      OverviewActionNames.SetDifferecePeriod,
      OverviewActionNames.SetSelectedRooms,
      OverviewActionNames.InitiateStates,
      OverviewActionNames.SetSelectedDates,
      UserAccountActions.LanguageSwitched,
    ],
    function*() {
      const selectedIndex = yield Effects.select(getOverviewSelectedIndex)
      const selectedPeriod = yield Effects.select(getOverviewDifferencePeriod)
      const selectedDates = yield Effects.select(getOverviewSelectedDates)
      const selectedRooms = yield Effects.select(getOverviewSelectedRooms)

      const rooms = yield Effects.select(getSortedRoomsByPosition)
      const events = yield select(notRemovedAndUnavailableEvents)

      const {
        from: startDate,
        to: finishDate,
        diffFrom,
        diffTo,
      } = selectedPeriod
        ? selectedPeriod
        : overviewPeriod(selectedIndex, selectedDates)

      const {
        ocupancy,
        income,
        daysDifference,
        eventsFilteredWithCancelled,
        eventsFilteredWithoutCancelled,
        eventsReservationDays,
        avgDays,
        avgDaysAb,
        bookingWindow,
      } = calculateEvents(events, selectedRooms, rooms, startDate, finishDate)

      let compare = undefined

      if (selectedPeriod) {
        compare = calculateEvents(
          events,
          selectedRooms,
          rooms,
          diffFrom,
          diffTo,
        )
      }

      yield Effects.put({
        type: OverviewActionNames.UpdateStats,
        payload: {
          diffStats: compare
            ? {
                ...compare,
                ADR: compare.income.total / compare.eventsReservationDays,
                events: compare.eventsFilteredWithoutCancelled,
                eventsCancelled: compare.eventsFilteredWithCancelled,
                RevPAR:
                  compare.income.total /
                  (compare.daysDifference *
                    (selectedRooms.length
                      ? selectedRooms.length
                      : rooms.length)),
              }
            : undefined,
          ocupancy,
          income,
          events: eventsFilteredWithoutCancelled,
          eventsCancelled: eventsFilteredWithCancelled,
          avgDays: avgDays,
          avgDaysAb: avgDaysAb,
          bookingWindow,
          ADR: income.total / eventsReservationDays,
          RevPAR:
            income.total /
            (daysDifference *
              (selectedRooms.length ? selectedRooms.length : rooms.length)),
        },
      })
    },
  )
}

const calculateEvents = (events, selectedRooms, rooms, startDate, fd) => {
  let eventsFiltered = []

  const finishDate = addDays(fd, 1)

  for (const event of events) {
    if (
      startDate &&
      finishDate &&
      (!inDateRange(addSeconds(event.startDate, 1), startDate, finishDate) &&
        !inDateRange(event.finishDate, startDate, finishDate)) &&
      (!inDateRange(startDate, event.startDate, event.finishDate) ||
        !inDateRange(finishDate, event.startDate, event.finishDate))
    ) {
      continue
    }

    if (selectedRooms.length === 0) {
      eventsFiltered.push(event)
    } else if (selectedRooms.includes(Number(event.roomId))) {
      eventsFiltered.push(event)
    }
  }

  const eventsFilteredWithoutCancelled = eventsFiltered.filter(
    e => e.stage !== EventStatus.Canceled,
  )
  const eventsFilteredWithCancelled = eventsFiltered.filter(
    e => e.stage === EventStatus.Canceled,
  )

  const {
    ocupancy,
    daysDifference,
    eventsReservationDays,
    totalDays,
    avgDays,
    avgDaysAb,
  } = calculateOcupancy(
    startDate,
    finishDate,
    selectedRooms.length ? selectedRooms.length : rooms.length,
    eventsFilteredWithoutCancelled,
  )

  const income = calculateIncome(
    startDate,
    finishDate,
    eventsFilteredWithoutCancelled,
  )

  const bookingWindow = calculateBookingWindow(eventsFilteredWithoutCancelled)

  return {
    bookingWindow,
    income,
    ocupancy,
    daysDifference,
    totalDays,
    eventsReservationDays,
    avgDays,
    avgDaysAb,
    eventsFilteredWithoutCancelled,
    eventsFilteredWithCancelled,
  }
}

const calculateBookingWindow = events => {
  let bookingWindow = 0

  events.forEach((e: IEvent) => {
    const diff = differenceInCalendarDays(e.startDate, e.createdAt)
    if (diff > -1) {
      bookingWindow += diff
    }
  })

  return bookingWindow / events.length
}

export const calculateIncomeGrouped = (startDate, finishDate, events) => {
  let groupedEvents: any = groupBy(events, 'roomId')

  Object.entries(groupedEvents).forEach(([key, value]) => {
    groupedEvents[key] = calculateIncome(startDate, finishDate, value)
  })

  return groupedEvents
}

export const calculateIncome = (startDate, finishDate, events) => {
  let additional = 0
  let recived = 0
  let awaits = 0

  events.forEach((e: IEvent) => {
    const daysInRange = differenceInCalendarDays(
      min(e.finishDate, finishDate),
      max(e.startDate, startDate),
    )

    const pirceperday =
      Number(e._price) / differenceInCalendarDays(e.finishDate, e.startDate)

    if (e.stage === EventStatus.Full) {
      recived += pirceperday * daysInRange
      if (e.additionalServices) {
        additional +=
          (e.additionalServices.reduce((a, b) => a + Number(b.price), 0) /
            differenceInCalendarDays(e.finishDate, e.startDate)) *
          daysInRange
      }
    } else if (e.stage === EventStatus.Advance) {
      recived +=
        (Number(e._provision) /
          differenceInCalendarDays(e.finishDate, e.startDate)) *
        daysInRange
      awaits +=
        pirceperday * daysInRange -
        (Number(e._provision) /
          differenceInCalendarDays(e.finishDate, e.startDate)) *
          daysInRange
      if (e.additionalServices) {
        additional +=
          (e.additionalServices.reduce((a, b) => a + Number(b.price), 0) /
            differenceInCalendarDays(e.finishDate, e.startDate)) *
          daysInRange
      }
    } else if (e.stage === EventStatus.NoPayment) {
      awaits += Number(e._pricePerDay) * daysInRange
      if (e.additionalServices) {
        additional +=
          (e.additionalServices.reduce((a, b) => a + Number(b.price), 0) /
            differenceInCalendarDays(e.finishDate, e.startDate)) *
          daysInRange
      }
    }
  })

  return {
    total: additional + recived + awaits,
    additional: additional,
    recived: recived,
    awaits: awaits,
  }
}

export const calculateOcupancy = (startDate, finishDate, rooms, events) => {
  const daysDifference = Math.abs(
    differenceInCalendarDays(finishDate, startDate),
  )
  const daysCount = daysDifference * rooms

  const eventsReservationDays = events.reduce(
    (a, b) =>
      a +
      differenceInCalendarDays(
        min(b.finishDate, addSeconds(finishDate, 1)),
        max(b.startDate, startDate),
      ),
    0,
  )

  const eventsReservationDaysAb = events.reduce(
    (a, b) => a + differenceInCalendarDays(b.finishDate, b.startDate),
    0,
  )

  return {
    totalDays: daysCount,
    daysDifference: daysDifference,
    eventsReservationDays: eventsReservationDays,
    avgDays: Math.round(eventsReservationDays / events.length),
    avgDaysAb: Math.round(eventsReservationDaysAb / events.length),
    ocupancy: Math.round((eventsReservationDays / daysCount) * 100),
  }
}
